/**
 * Attachments — the FDR document spine's one shared surface.
 *
 * Drop a file anywhere on the panel, paste a screenshot straight from the
 * clipboard, or click to browse. Every file gets its own row with real
 * progress, inline preview (PDF/images), download, replace-with-new-version,
 * an expiry date that drives renewal, and delete-with-reason. Rejections say
 * exactly what is wrong ("PDF up to 25 MB; this file is 40 MB"), never
 * "upload failed". PHI-quarantined files show the quarantine notice.
 *
 * Mount it against any FDR record:
 *   <Attachments entityId={id} recordKind="finding" recordId={findingId} />
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_API_URL;
const MAX_MB = 25;
const ALLOWED = /\.(pdf|docx?|xlsx?|csv|txt|md|png|jpe?g)$/i;
const PREVIEWABLE = /^(application\/pdf|image\/(png|jpe?g))/i;

const fmtSize = (b) => b == null ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const fmtDate = (d) => {
  if (!d) return '';
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : new Date(s).toLocaleDateString();
};
const daysUntil = (d) => {
  if (!d) return null;
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const target = m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : new Date(d);
  return Math.floor((target - Date.now()) / 86400000);
};

function fileIcon(mime, name) {
  const n = (name || '').toLowerCase();
  if (/pdf/.test(mime) || n.endsWith('.pdf')) return '📄';
  if (/image/.test(mime) || /\.(png|jpe?g)$/.test(n)) return '🖼️';
  if (/excel|sheet/.test(mime) || /\.(xlsx?|csv)$/.test(n)) return '📊';
  if (/word/.test(mime) || /\.docx?$/.test(n)) return '📝';
  return '📎';
}

function validateFile(file) {
  if (!ALLOWED.test(file.name || '')) {
    return `"${file.name}" is not a supported type. Use PDF, Word, Excel, CSV, text, or an image.`;
  }
  if (file.size > MAX_MB * 1048576) {
    return `Files can be up to ${MAX_MB} MB; "${file.name}" is ${(file.size / 1048576).toFixed(1)} MB.`;
  }
  return null;
}

export default function Attachments({ entityId, recordKind = 'entity', recordId = null, category = 'general', title = 'Documents', compact = false }) {
  const [docs, setDocs] = useState([]);
  const [queue, setQueue] = useState([]);           // [{key, name, pct, error}]
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);      // {id, name, mime}
  const [editingExpiry, setEditingExpiry] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const replacingId = useRef(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    const qs = new URLSearchParams({ entity_id: entityId, record_kind: recordKind });
    if (recordId) qs.set('record_id', recordId);
    try {
      const r = await fetch(`${API}/api/fdr/documents?${qs}`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setDocs(r.documents || []);
    } catch { /* keep prior state */ }
    setLoaded(true);
  }, [entityId, recordKind, recordId]);
  useEffect(() => { load(); }, [load]);

  // Poll briefly while any doc is still extracting, so "Reading text…"
  // resolves (or a quarantine shows) without a manual refresh.
  useEffect(() => {
    if (!docs.some(d => ['pending', 'extracting'].includes(d.extraction_status))) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [docs, load]);

  const uploadOne = useCallback((file, replacesId = null) => {
    const err = validateFile(file);
    const key = `${file.name}-${Date.now()}-${Math.random()}`;
    if (err) {
      setQueue(q => [...q, { key, name: file.name, pct: 0, error: err }]);
      return;
    }
    setQueue(q => [...q, { key, name: file.name, pct: 0 }]);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('entity_id', entityId);
    fd.append('record_kind', recordKind);
    if (recordId) fd.append('record_id', recordId);
    fd.append('category', category);
    if (replacesId) fd.append('replaces_id', replacesId);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/fdr/documents`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setQueue(q => q.map(x => x.key === key ? { ...x, pct: Math.round((e.loaded / e.total) * 100) } : x));
    };
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON error */ }
      if (xhr.status >= 200 && xhr.status < 300 && body.success) {
        setQueue(q => q.filter(x => x.key !== key));
        load();
      } else {
        setQueue(q => q.map(x => x.key === key ? { ...x, error: body.message || `Upload failed (${xhr.status})` } : x));
      }
    };
    xhr.onerror = () => setQueue(q => q.map(x => x.key === key ? { ...x, error: 'Network error — check your connection and retry.' } : x));
    xhr.send(fd);
  }, [entityId, recordKind, recordId, category, load]);

  const takeFiles = useCallback((fileList) => {
    Array.from(fileList || []).forEach(f => uploadOne(f));
  }, [uploadOne]);

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); takeFiles(e.dataTransfer.files); };
  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); takeFiles(files); }
  };

  const setExpiry = async (id, value) => {
    setEditingExpiry(null);
    await fetch(`${API}/api/fdr/documents/${id}`, {
      method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expires_at: value || null }),
    }).catch(() => {});
    load();
  };

  const remove = async (doc) => {
    const reason = window.prompt(`Remove "${doc.file_name}"? Type a short reason — it becomes the record.`);
    if (reason == null) return;
    const r = await fetch(`${API}/api/fdr/documents/${doc.id}`, {
      method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    }).then(r => r.json()).catch(() => ({}));
    if (!r.success) window.alert(r.message || 'Could not remove the file');
    load();
  };

  const startReplace = (doc) => { replacingId.current = doc.id; replaceRef.current?.click(); };

  const chip = (cls, label) => <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${cls}`}>{label}</span>;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
      onDrop={onDrop} onPaste={onPaste} tabIndex={-1}
      className={`rounded-xl border transition-colors ${dragOver ? 'border-orange-400 bg-orange-50/60 ring-2 ring-orange-200' : 'border-slate-200 bg-white'}`}
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="text-sm font-semibold text-slate-700">{title}
          {docs.length > 0 && <span className="text-slate-400 font-normal"> · {docs.length}</span>}
        </div>
        <button onClick={() => inputRef.current?.click()}
          className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
          + Add file
        </button>
        <input ref={inputRef} type="file" multiple className="hidden"
          onChange={(e) => { takeFiles(e.target.files); e.target.value = ''; }} />
        <input ref={replaceRef} type="file" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadOne(f, replacingId.current); e.target.value = ''; replacingId.current = null; }} />
      </div>

      <div className="px-4 pb-3 space-y-1.5">
        {queue.map(u => (
          <div key={u.key} className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${u.error ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
            <span className="text-base">📎</span>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-slate-700 truncate">{u.name}</div>
              {u.error
                ? <div className="text-[11px] text-red-600">{u.error}</div>
                : <div className="h-1.5 mt-1 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-orange-500 transition-all" style={{ width: `${u.pct}%` }} /></div>}
            </div>
            {u.error && <button onClick={() => setQueue(q => q.filter(x => x.key !== u.key))} className="text-[11px] text-slate-400 hover:text-slate-600">Dismiss</button>}
          </div>
        ))}

        {docs.map(d => {
          const quarantined = d.extraction_status === 'blocked_phi';
          const reading = ['pending', 'extracting'].includes(d.extraction_status);
          const dleft = daysUntil(d.expires_at);
          return (
            <div key={d.id} className="group flex items-center gap-3 rounded-lg px-3 py-2 border border-slate-100 hover:border-slate-200 hover:bg-slate-50/60">
              <span className="text-base">{quarantined ? '🚫' : fileIcon(d.mime_type, d.file_name)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-700 truncate">{d.label || d.file_name}</span>
                  {d.version > 1 && chip('bg-slate-100 text-slate-500 border-slate-200', `v${d.version}`)}
                  {d.source === 'vendor' && chip('bg-sky-50 text-sky-700 border-sky-200', 'From vendor')}
                  {quarantined && chip('bg-red-50 text-red-700 border-red-200', 'PHI — quarantined')}
                  {reading && chip('bg-amber-50 text-amber-700 border-amber-200', 'Reading text…')}
                  {dleft != null && dleft < 0 && chip('bg-red-50 text-red-700 border-red-200', `Expired ${fmtDate(d.expires_at)}`)}
                  {dleft != null && dleft >= 0 && dleft <= 30 && chip('bg-amber-50 text-amber-700 border-amber-200', `Expires in ${dleft}d`)}
                  {dleft != null && dleft > 30 && chip('bg-slate-50 text-slate-500 border-slate-200', `Expires ${fmtDate(d.expires_at)}`)}
                </div>
                <div className="text-[11px] text-slate-400">
                  {fmtSize(d.file_size_bytes)} · {d.source === 'vendor' ? (d.vendor_contact_name || d.vendor_contact_email || 'vendor') : (d.uploaded_by_name || 'you')} · {fmtDate(d.created_at)}
                  {quarantined && <span className="text-red-500"> · contents destroyed, Compliance alerted</span>}
                </div>
              </div>
              {!quarantined && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  {PREVIEWABLE.test(d.mime_type || '') && (
                    <button onClick={() => setPreview(d)} title="Preview"
                      className="px-2 py-1 text-[11px] font-semibold rounded-md text-slate-500 hover:bg-slate-100">Preview</button>
                  )}
                  <a href={`${API}/api/fdr/documents/${d.id}/download`} title="Download"
                    className="px-2 py-1 text-[11px] font-semibold rounded-md text-slate-500 hover:bg-slate-100">Download</a>
                  {editingExpiry === d.id ? (
                    <input type="date" autoFocus defaultValue={d.expires_at ? String(d.expires_at).slice(0, 10) : ''}
                      onBlur={(e) => setExpiry(d.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setExpiry(d.id, e.target.value); if (e.key === 'Escape') setEditingExpiry(null); }}
                      className="text-[11px] border border-slate-300 rounded-md px-1.5 py-1" />
                  ) : (
                    <button onClick={() => setEditingExpiry(d.id)} title="Set expiry date"
                      className="px-2 py-1 text-[11px] font-semibold rounded-md text-slate-500 hover:bg-slate-100">Expiry</button>
                  )}
                  <button onClick={() => startReplace(d)} title="Upload a new version"
                    className="px-2 py-1 text-[11px] font-semibold rounded-md text-slate-500 hover:bg-slate-100">Replace</button>
                  <button onClick={() => remove(d)} title="Remove (reason required)"
                    className="px-2 py-1 text-[11px] font-semibold rounded-md text-red-400 hover:bg-red-50 hover:text-red-600">Remove</button>
                </div>
              )}
            </div>
          );
        })}

        {loaded && !docs.length && !queue.length && (
          <button onClick={() => inputRef.current?.click()}
            className={`w-full text-center border border-dashed rounded-xl px-4 ${compact ? 'py-4' : 'py-7'} ${dragOver ? 'border-orange-300' : 'border-slate-200'} text-slate-400 hover:border-slate-300 hover:text-slate-500`}>
            <div className="text-sm font-medium">Drop files here, paste from clipboard, or click to browse</div>
            <div className="text-[11px] mt-1">PDF, Word, Excel, CSV, text, or images · up to {MAX_MB} MB · text files are screened for PHI</div>
          </button>
        )}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
              <div className="text-sm font-semibold text-slate-700 truncate">{preview.file_name}</div>
              <div className="flex items-center gap-2">
                <a href={`${API}/api/fdr/documents/${preview.id}/download`} className="text-xs font-semibold text-orange-600 hover:underline">Download</a>
                <button onClick={() => setPreview(null)} className="px-2 py-1 text-sm text-slate-400 hover:text-slate-600">✕</button>
              </div>
            </div>
            {/^image\//i.test(preview.mime_type || '')
              ? <div className="flex-1 overflow-auto flex items-center justify-center bg-slate-50"><img src={`${API}/api/fdr/documents/${preview.id}/preview`} alt={preview.file_name} className="max-w-full max-h-full object-contain" /></div>
              : <iframe title={preview.file_name} src={`${API}/api/fdr/documents/${preview.id}/preview`} className="flex-1 w-full" />}
          </div>
        </div>
      )}
    </div>
  );
}
